/**
 * Health (RAG) thresholds — the single source of truth for red/amber/green
 * banding across the app (status-report KPI tiles and the insights bell).
 *
 * Defaults match the insights engine's historical cutoffs (CPI/SPI 0.9 / 0.8;
 * over budget at 100%, critical at 20% over). They can be overridden per
 * workspace via Settings → Health thresholds; callers pass the workspace's
 * values in, falling back to DEFAULT_HEALTH_THRESHOLDS.
 */

export type Rag = 'success' | 'warning' | 'danger' | 'neutral';

export interface HealthThresholds {
  /** CPI/SPI at or above this → green. */
  cpiSpiGreen: number;
  /** CPI/SPI below this → red; between red and green → amber. */
  cpiSpiRed: number;
  /** Budget utilisation (%) above this → amber (over budget). */
  budgetAmberPct: number;
  /** Budget utilisation (%) at or above this → red (materially over budget). */
  budgetRedPct: number;
  /** Resource utilisation (%) at or above this → amber (heavily loaded). */
  utilAmberPct: number;
  /** Resource utilisation (%) at or above this → red (over-allocated). */
  utilRedPct: number;
}

export const DEFAULT_HEALTH_THRESHOLDS: HealthThresholds = {
  cpiSpiGreen: 0.9,
  cpiSpiRed: 0.8,
  budgetAmberPct: 100,
  budgetRedPct: 120,
  utilAmberPct: 80,
  utilRedPct: 100,
};

/** Sensible guardrails so the Settings editor can validate input. */
export const HEALTH_THRESHOLD_BOUNDS = {
  cpiSpiGreen: { min: 0.5, max: 1.5, step: 0.01 },
  cpiSpiRed: { min: 0.3, max: 1.2, step: 0.01 },
  budgetAmberPct: { min: 50, max: 200, step: 1 },
  budgetRedPct: { min: 60, max: 300, step: 1 },
  utilAmberPct: { min: 40, max: 150, step: 1 },
  utilRedPct: { min: 50, max: 200, step: 1 },
} as const;

/** Clamp + order-correct a thresholds object so red < green and amber < red%. */
export function normalizeHealthThresholds(t: Partial<HealthThresholds>): HealthThresholds {
  const merged = { ...DEFAULT_HEALTH_THRESHOLDS, ...t };
  // Keep the red cutoff strictly below green, and amber% below red%.
  const cpiSpiRed = Math.min(merged.cpiSpiRed, merged.cpiSpiGreen - 0.01);
  const budgetAmberPct = Math.min(merged.budgetAmberPct, merged.budgetRedPct - 1);
  const utilAmberPct = Math.min(merged.utilAmberPct, merged.utilRedPct - 1);
  return {
    cpiSpiGreen: merged.cpiSpiGreen,
    cpiSpiRed,
    budgetAmberPct,
    budgetRedPct: merged.budgetRedPct,
    utilAmberPct,
    utilRedPct: merged.utilRedPct,
  };
}

/** CPI / SPI value → RAG. */
export function perfRag(value: number, t: HealthThresholds = DEFAULT_HEALTH_THRESHOLDS): Rag {
  if (value >= t.cpiSpiGreen) return 'success';
  if (value >= t.cpiSpiRed) return 'warning';
  return 'danger';
}

/** Budget utilisation (spent/budget %) → RAG. */
export function budgetRag(
  utilizationPct: number,
  hasBudget: boolean,
  t: HealthThresholds = DEFAULT_HEALTH_THRESHOLDS,
): Rag {
  if (!hasBudget) return 'neutral';
  if (utilizationPct >= t.budgetRedPct) return 'danger';
  if (utilizationPct > t.budgetAmberPct) return 'warning';
  return 'success';
}

/**
 * Resource utilisation (%) → RAG. Higher is worse (opposite of CPI/SPI): at or
 * above the red cutoff → over-allocated (red), at or above amber → heavily
 * loaded (amber), otherwise healthy headroom (green).
 */
export function utilizationRag(
  utilizationPct: number,
  t: HealthThresholds = DEFAULT_HEALTH_THRESHOLDS,
): Rag {
  if (utilizationPct >= t.utilRedPct) return 'danger';
  if (utilizationPct >= t.utilAmberPct) return 'warning';
  return 'success';
}

/** Most severe of several RAG values. */
export function worstRag(...rags: Rag[]): Rag {
  if (rags.includes('danger')) return 'danger';
  if (rags.includes('warning')) return 'warning';
  if (rags.includes('success')) return 'success';
  return 'neutral';
}

/** Tailwind text-colour class for a RAG tone. */
export function ragTextClass(tone: Rag): string {
  if (tone === 'success') return 'text-success-dark';
  if (tone === 'warning') return 'text-warning-dark';
  if (tone === 'danger') return 'text-danger';
  return 'text-ink-primary';
}
